import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth, handleFirestoreError, OperationType } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Shapes, AlertTriangle, CheckCircle2, UserPlus } from 'lucide-react';
import { AuthModal } from './AuthModal';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get('inviteId');
  const navigate = useNavigate();
  const { user, loading, isAuthModalOpen, openAuth } = useAuth();

  const [inviteState, setInviteState] = useState<'loading' | 'invalid' | 'expired' | 'revoked' | 'accepted' | 'ready' | 'mismatch' | 'success'>('loading');
  const [inviteData, setInviteData] = useState<any>(null);
  const [errorString, setErrorString] = useState<string | null>(null);

  useEffect(() => {
    async function checkInvite() {
      if (!inviteId) {
        setInviteState('invalid');
        return;
      }

      try {
        const inviteDocRef = doc(db, 'organisation_invites', inviteId);
        const inviteDoc = await getDoc(inviteDocRef);

        if (!inviteDoc.exists()) {
          setInviteState('invalid');
          return;
        }

        const data = inviteDoc.data();
        setInviteData(data);

        if (data.status === 'revoked') {
          setInviteState('revoked');
          return;
        }

        if (data.status === 'accepted') {
          setInviteState('accepted');
          return;
        }

        const now = new Date();
        const expiresAt = new Date(data.expiresAt);
        if (now > expiresAt || data.status === 'expired') {
          setInviteState('expired');
          return;
        }

        if (!user) {
          setInviteState('ready'); // Ready for login
          return;
        }

        if (user.email?.toLowerCase() !== data.invitedEmail.toLowerCase()) {
          setInviteState('mismatch');
          return;
        }

        // Ready to accept automatically
        await acceptInvite(data);

      } catch (err: any) {
        console.error("Invite fetch error", err);
        setInviteState('invalid');
        setErrorString(`Read failed: ${err.message}`);
      }
    }

    if (!loading) {
      checkInvite();
    }
  }, [inviteId, user, loading]);

  const acceptInvite = async (data: any) => {
    if (!user || user.email?.toLowerCase() !== data.invitedEmail.toLowerCase()) return;
    
    setInviteState('loading');
    try {
      const orgId = data.organisationId;
      const memberId = `${user.email?.toLowerCase()}_${orgId}`;

      // Create member
      await setDoc(doc(db, 'organisation_members', memberId), {
        orgId: orgId,
        userId: user.uid,
        email: user.email?.toLowerCase(),
        role: data.role,
        status: 'active',
        inviteId: data.inviteId,
        joinedAt: serverTimestamp()
      });

      // Update invite
      const inviteDocRef = doc(db, 'organisation_invites', data.inviteId);
      await setDoc(inviteDocRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        acceptedByUserId: user.uid
      }, { merge: true });

      setInviteState('success');
      setTimeout(() => {
        navigate('/organisation');
      }, 2000);
    } catch (err: any) {
      console.error("Failed to accept invite:", err);
      // We do NOT call handleFirestoreError because it throws and breaks the UI flow
      setErrorString(`Write failed: ${err.message}`);
      setInviteState('invalid');
    }
  };

  if (loading || inviteState === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-bg-main p-4">
        <div className="h-16 w-16 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
          <Shapes className="h-8 w-8 text-indigo-500 animate-pulse" />
        </div>
        <p className="mt-4 text-text-alt font-medium">Validating invite...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-bg-main p-4 text-center" data-clarity-mask="true">
      <div className="bg-bg-alt border border-border-main p-8 rounded-3xl max-w-md shadow-2xl w-full">
        {inviteState === 'invalid' && (
          <>
            <div className="h-16 w-16 bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-rose-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Invalid Invite Link</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              {errorString || "This invite link is invalid or does not exist."}
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full py-4 bg-bg-main hover:bg-border-main border border-border-main text-text-main font-bold rounded-2xl transition-all"
            >
              Return Home
            </button>
          </>
        )}

        {inviteState === 'expired' && (
          <>
            <div className="h-16 w-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Invite Expired</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              This invite link has expired. Please contact the organisation admin for a new invite.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full py-4 bg-bg-main hover:bg-border-main border border-border-main text-text-main font-bold rounded-2xl transition-all"
            >
              Return Home
            </button>
          </>
        )}

        {inviteState === 'revoked' && (
          <>
            <div className="h-16 w-16 bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-rose-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Invite Revoked</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              This invite has been revoked by the organisation admin.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full py-4 bg-bg-main hover:bg-border-main border border-border-main text-text-main font-bold rounded-2xl transition-all"
            >
              Return Home
            </button>
          </>
        )}

        {inviteState === 'accepted' && (
          <>
            <div className="h-16 w-16 bg-indigo-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-indigo-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Already Accepted</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              This invite has already been accepted.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {inviteState === 'mismatch' && (
          <>
            <div className="h-16 w-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Email Mismatch</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              {inviteData?.invitedEmail ? (
                <>This invite was sent to <strong className="text-text-main">{inviteData.invitedEmail}</strong>, but you are signed in as <strong className="text-text-main">{user?.email}</strong>.</>
              ) : (
                <>You are currently signed in as <strong className="text-text-main">{user?.email}</strong>, which does not match the invited email address or lacks permission to view it.</>
              )}
              <br /><br />
              Please sign out and sign in with the correct email address to accept this invite.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={async () => { await auth.signOut(); openAuth('login'); }}
                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-500/20 active:scale-95"
              >
                Sign Out & Switch Account
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full py-4 bg-bg-main hover:bg-border-main border border-border-main text-text-main font-bold rounded-2xl transition-all"
              >
                Return Home
              </button>
            </div>
          </>
        )}

        {inviteState === 'ready' && !user && (
          <>
            <div className="h-16 w-16 bg-indigo-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <UserPlus className="h-8 w-8 text-indigo-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">You've been invited!</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              You've been invited to join <strong className="text-text-main font-bold">{inviteData?.organisationName}</strong> on Lumina Learn.
              <br /><br />
              Please sign in or sign up with <strong className="text-text-main">{inviteData?.invitedEmail}</strong> to accept your invite.
            </p>
            <button
              onClick={() => openAuth('register')}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-500/20 active:scale-95"
            >
              Sign In to Accept
            </button>
          </>
        )}

        {inviteState === 'success' && (
          <>
            <div className="h-16 w-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Invite Accepted!</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              You are now a member of <strong className="text-text-main">{inviteData?.organisationName}</strong>.
              Redirecting you to the organisation dashboard...
            </p>
          </>
        )}
      </div>

      {isAuthModalOpen && <AuthModal />}
    </div>
  );
}
